import type { TokenPair } from "@/shared/auth/types";

export type TokenStore = {
  get(): TokenPair | null;
  set(pair: TokenPair, options?: { persist?: boolean }): void;
  clear(): void;
  /** Last persist preference (remember me). Default true when unset. */
  getPersistPreference(): boolean;
};

const TOKENS_KEY = "galaxy.auth.tokens";
const PERSIST_KEY = "galaxy.auth.persist";
const USERNAME_KEY = "galaxy.auth.rememberUsername";

function readPair(storage: Storage): TokenPair | null {
  try {
    const raw = storage.getItem(TOKENS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TokenPair;
    if (!parsed.accessToken || !parsed.refreshToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePair(storage: Storage, pair: TokenPair): void {
  storage.setItem(TOKENS_KEY, JSON.stringify(pair));
}

function readPersistPreference(): boolean {
  const flag = localStorage.getItem(PERSIST_KEY);
  if (flag === "0") return false;
  if (flag === "1") return true;
  // Default true when unset (as documented in TokenStore interface)
  return true;
}

/** Persist across browser restarts (localStorage) or tab session only (sessionStorage). */
export function createBrowserTokenStore(): TokenStore {
  return {
    getPersistPreference: readPersistPreference,
    get() {
      return readPair(localStorage) ?? readPair(sessionStorage);
    },
    set(pair, options) {
      const persist = options?.persist ?? readPersistPreference();
      localStorage.setItem(PERSIST_KEY, persist ? "1" : "0");
      // Always keep tokens in localStorage so all tabs share the active session
      writePair(localStorage, pair);
      if (!persist) {
        writePair(sessionStorage, pair);
      } else {
        sessionStorage.removeItem(TOKENS_KEY);
      }
    },
    clear() {
      localStorage.removeItem(TOKENS_KEY);
      sessionStorage.removeItem(TOKENS_KEY);
      // Keep PERSIST_KEY + username so next login form defaults match last choice.
    },
  };
}

/** @deprecated Prefer createBrowserTokenStore — alias for existing callers. */
export function createLocalStorageTokenStore(): TokenStore {
  return createBrowserTokenStore();
}

export function createMemoryTokenStore(): TokenStore {
  let current: TokenPair | null = null;
  let persist = true;
  return {
    getPersistPreference: () => persist,
    get: () => current,
    set: (pair, options) => {
      if (options?.persist !== undefined) persist = options.persist;
      current = pair;
    },
    clear: () => {
      current = null;
    },
  };
}

export function getRememberedUsername(): string {
  try {
    return localStorage.getItem(USERNAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setRememberedUsername(username: string | null): void {
  try {
    if (username?.trim()) {
      localStorage.setItem(USERNAME_KEY, username.trim());
    } else {
      localStorage.removeItem(USERNAME_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
}
